import React from 'react';
import SectionLabel from './SectionLabel';

const cases = [
  {
    sector: 'Hospitality / Lifestyle Group',
    client: 'Komaro Group',
    clientSub: 'Hotel Komaro · Komaro Water · Komaro Energies\nFloating Restaurant & Lounge',
    metric: 'Multi-Brand',
    metricLabel: 'Strengthened digital visibility & booking inquiry pathways',
    desc: 'Built and strengthened the digital growth structure for a multi-brand hospitality and lifestyle group across hotels, serviced apartments, water, energy, restaurant, lounge, and event-led experiences. HNL supported brand visibility, social media systems, content direction, Google Business presence, campaign planning, listing structure, and conversion-focused digital touchpoints designed to turn attention into inquiries, bookings, foot traffic, and stronger brand recall.',
  },
  {
    sector: 'Fashion & Beauty',
    client: 'Ciar & Konyin',
    clientSub: 'Skincare · Fast Fashion',
    metric: '+380%',
    metricLabel: 'Increase in content-driven e-commerce revenue',
    desc: 'Repositioned the brand in a saturated market, rebuilt the content system from editorial direction through to paid amplification, and built the email retention infrastructure that converted first-time buyers into repeat customers.',
  },
  {
    sector: 'Real Estate',
    client: 'Gyimah Kyei',
    clientSub: 'Full Digital Build',
    metric: 'Full',
    metricLabel: 'Digital presence built from zero',
    desc: 'Delivered a complete digital build: brand identity, premium website, content strategy, social media architecture, and lead generation system — positioning the advisory as a premium, authority-led property brand in a competitive market.',
  },
];

const WorkCard: React.FC<typeof cases[0]> = ({ sector, client, clientSub, metric, metricLabel, desc }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        borderRadius: 4,
        padding: '40px 34px',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(255,248,225,0.38)',
        border: `1px solid ${hovered ? 'rgba(184,154,90,0.55)' : 'rgba(184,154,90,0.22)'}`,
        boxShadow: hovered
          ? '0 16px 48px rgba(139,110,50,0.18), 0 4px 12px rgba(139,110,50,0.1), inset 0 1px 0 rgba(255,255,255,0.7)'
          : '0 4px 24px rgba(139,110,50,0.1), 0 1px 4px rgba(139,110,50,0.07), inset 0 1px 0 rgba(255,255,255,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'box-shadow 0.32s cubic-bezier(0.22,1,0.36,1), border-color 0.32s ease, transform 0.32s cubic-bezier(0.22,1,0.36,1)',
        cursor: 'default',
        overflow: 'hidden',
      }}
    >
      {/* Left border glow — slides in on hover */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '10%',
          bottom: '10%',
          width: 2,
          background: 'linear-gradient(to bottom, transparent, #b89a5a, transparent)',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.35s ease',
        }}
      />

      {/* Spotlight sweep — radiates from centre on hover */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 70% 65% at 50% 40%, rgba(212,184,122,0.18) 0%, transparent 70%)',
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'scale(1)' : 'scale(0.8)',
          transition: 'opacity 0.4s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)',
          pointerEvents: 'none',
        }}
      />

      {/* Card content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Sector */}
        <div
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 8.5,
            fontWeight: 600,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: '#b89a5a',
            marginBottom: 18,
          }}
        >
          {sector}
        </div>

        {/* Client name */}
        <div
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 21,
            fontWeight: 400,
            color: hovered ? '#6b4f10' : '#1a130a',
            lineHeight: 1.2,
            marginBottom: 6,
            transition: 'color 0.25s ease',
          }}
        >
          {client}
        </div>

        {/* Client sub */}
        {clientSub.split('\n').map((line, i) => (
          <span
            key={i}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 11.5,
              fontWeight: 300,
              color: '#7a6e56',
              lineHeight: 1.7,
              display: 'block',
              marginBottom: i === clientSub.split('\n').length - 1 ? 22 : 0,
            }}
          >
            {line}
          </span>
        ))}

        {/* Metric */}
        <div
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 42,
            fontWeight: 300,
            color: '#b89a5a',
            lineHeight: 1,
            marginBottom: 6,
            letterSpacing: '-0.01em',
          }}
        >
          {metric}
        </div>

        <div
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#8a7040',
            marginBottom: 24,
          }}
        >
          {metricLabel}
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: 'rgba(184,154,90,0.2)',
            marginBottom: 20,
          }}
        />

        {/* Description */}
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 11.5,
            fontWeight: 300,
            color: '#6b5f46',
            lineHeight: 1.95,
            letterSpacing: '0.02em',
            flex: 1,
          }}
        >
          {desc}
        </p>
      </div>
    </div>
  );
};

const Work: React.FC = () => (
  <section
    id="work"
    className="relative py-[120px] px-10"
    style={{
      background: 'linear-gradient(158deg, #faf4e8 0%, #f5ecce 38%, #ede0b4 72%, #e8d89e 100%)',
      borderTop: '1px solid rgba(184,154,90,0.18)',
      borderBottom: '1px solid rgba(184,154,90,0.18)',
    }}
  >
    <div className="max-w-[1200px] mx-auto relative z-10">
      {/* Header row */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-[64px] gap-6">
        <div>
          <SectionLabel>Selected Work</SectionLabel>
          <h2
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(34px, 4.5vw, 58px)',
              fontWeight: 300,
              lineHeight: 1.1,
              color: '#1a130a',
            }}
          >
            Real Brands.
            <br />
            <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>Real Structure.</em>
          </h2>
        </div>

        <button
          className="cursor-pointer transition-all duration-200"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#5a4e38',
            padding: '13px 30px',
            background: 'transparent',
            border: '1px solid rgba(184,154,90,0.4)',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#b89a5a';
            e.currentTarget.style.color = '#8a6820';
            e.currentTarget.style.background = 'rgba(184,154,90,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(184,154,90,0.4)';
            e.currentTarget.style.color = '#5a4e38';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          View All Work
        </button>
      </div>

      {/* Cards */}
      <div
        className="grid grid-cols-1 md:grid-cols-3"
        style={{ gap: 22 }}
      >
        {cases.map((c) => (
          <WorkCard key={c.client} {...c} />
        ))}
      </div>
    </div>
  </section>
);

export default Work;